import { type FC, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useTheme } from "../../context/ThemeContext";
import { getChartTheme } from "../../theme/chartTheme";
import {
  TIMING_KIND_LABELS,
  type TimingEntry,
  type TimingKind,
} from "../../types/timings";
import { formatDurationMs } from "../../utils/time";
import { Button } from "../common/Button";
import { ContentCard } from "../common/ContentCard";
import { Text } from "../common/Text";

export interface TimingsDurationByKindChartProps {
  entries: TimingEntry[];
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface KindDurationStats {
  kind: TimingKind;
  label: string;
  avg: number;
  max: number;
  count: number;
}

function buildKindDurationStats(entries: TimingEntry[]): KindDurationStats[] {
  const map = new Map<
    TimingKind,
    { sum: number; max: number; count: number }
  >();

  for (const entry of entries) {
    const current = map.get(entry.kind);
    if (current) {
      current.sum += entry.duration_ms;
      current.max = Math.max(current.max, entry.duration_ms);
      current.count += 1;
    } else {
      map.set(entry.kind, {
        count: 1,
        max: entry.duration_ms,
        sum: entry.duration_ms,
      });
    }
  }

  return [...map.entries()]
    .map(([kind, stats]) => ({
      avg: stats.count > 0 ? stats.sum / stats.count : 0,
      count: stats.count,
      kind,
      label: TIMING_KIND_LABELS[kind],
      max: stats.max,
    }))
    .toSorted((a, b) => b.max - a.max);
}

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

interface TooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
  payload: KindDurationStats;
}

interface DurationTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  isDark: boolean;
}

const DurationTooltip: FC<DurationTooltipProps> = ({
  active,
  payload,
  isDark,
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const item = payload[0].payload;

  return (
    <div
      className="rounded-none border px-2.5 py-1.5 text-xs shadow-sm"
      style={{
        backgroundColor: getChartTheme(isDark).tooltipBg,
        borderColor: getChartTheme(isDark).tooltipBorder,
        color: getChartTheme(isDark).tooltipText,
      }}
    >
      <div style={{ color: getChartTheme(isDark).tooltipMuted }}>
        {item.label} &middot; {item.count.toLocaleString()}{" "}
        {item.count === 1 ? "entry" : "entries"}
      </div>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-1.5">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="font-semibold">
            {entry.name}: {formatDurationMs(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AXIS_TICK_STYLE = { fontSize: 10 };

export const TimingsDurationByKindChart: FC<
  TimingsDurationByKindChartProps
> = ({ entries }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const stats = useMemo(() => buildKindDurationStats(entries), [entries]);

  const { wideRange, maxDuration, logTicks } = useMemo(() => {
    if (stats.length === 0) {
      return { logTicks: [] as number[], maxDuration: 0, wideRange: false };
    }
    const allValues = stats.flatMap((s) => [s.avg, s.max]).filter((v) => v > 0);
    const minV = allValues.length > 0 ? Math.min(...allValues) : 1;
    const maxV = allValues.length > 0 ? Math.max(...allValues) : 1;
    const wide = maxV / minV > 50;
    const ticks: number[] = [];
    if (wide) {
      for (let p = 0; p <= Math.ceil(Math.log10(maxV * 1.2)); p++) {
        const v = 10 ** p;
        if (v <= maxV * 1.2) {
          ticks.push(v);
        }
      }
    }
    return { logTicks: ticks, maxDuration: maxV, wideRange: wide };
  }, [stats]);

  const [userScale, setUserScale] = useState<"log" | "linear" | null>(null);
  const logScale = userScale === null ? wideRange : userScale === "log";

  if (stats.length === 0) {
    return null;
  }

  const { axisText, gridLine } = getChartTheme(isDark);
  const cursorFill = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";
  const avgFill = isDark
    ? "rgba(16, 185, 129, 0.60)"
    : "rgba(5, 150, 105, 0.72)";
  const maxFill = isDark
    ? "rgba(245, 158, 11, 0.60)"
    : "rgba(217, 119, 6, 0.70)";

  return (
    <ContentCard className="space-y-3">
      <div className="flex items-center justify-between">
        <Text variant="label" as="div">
          Duration by kind
        </Text>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setUserScale(logScale ? "linear" : "log")}
          className="min-w-[5rem] justify-center px-2 py-1 text-slate-500 hover:text-emerald-700 dark:text-slate-400 dark:hover:text-emerald-300"
        >
          {logScale ? "Log" : "Linear"}
        </Button>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart
          data={stats}
          margin={{ bottom: 0, left: -16, right: 4, top: 4 }}
        >
          <CartesianGrid vertical={false} stroke={gridLine} />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK_STYLE}
            stroke={axisText}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={AXIS_TICK_STYLE}
            stroke={axisText}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => formatDurationMs(v)}
            scale={logScale ? "log" : "auto"}
            domain={logScale ? [1, maxDuration * 1.2] : undefined}
            ticks={logScale ? logTicks : undefined}
            allowDataOverflow={logScale}
          />
          <Tooltip
            cursor={{ fill: cursorFill }}
            content={<DurationTooltip isDark={isDark} />}
          />
          <Legend
            iconType="rect"
            iconSize={8}
            wrapperStyle={{ color: axisText, fontSize: 11 }}
          />
          <Bar
            name="Avg"
            dataKey="avg"
            radius={[0, 0, 0, 0]}
            maxBarSize={28}
            fill={avgFill}
          />
          <Bar
            name="Max"
            dataKey="max"
            radius={[0, 0, 0, 0]}
            maxBarSize={28}
            fill={maxFill}
          />
        </BarChart>
      </ResponsiveContainer>
    </ContentCard>
  );
};
